import type { QueryOptions } from 'sync/query';
import { defaultPaginationQuery } from 'sync/query';
import { deleteRequest, getRequest, postRequest, putRequest } from 'sync/request';
import type { OrganizationContext, ProjectContext, ProjectIntegrationContext, ProjectUserContext } from 'sync/request.types';
import {
  type AvailableInviteesResponse,
  type CreateProjectIntegrationPayload,
  CreateProjectIntegrationResponse,
  ProjectAPIKeyContext,
  ProjectAPIKeyCreateResponse,
  ProjectAPIKeyCreationPayload,
  ProjectAPIKeyListResponse,
  ProjectAPIKeyResponse,
  ProjectClusterSnapshotListResponse,
  type ProjectCreationPayload,
  type ProjectCreationResponse,
  ProjectIntegration,
  type ProjectIntegrationListResponse,
  type ProjectListResponse,
  type ProjectResponse,
  type ProjectUpdatePayload,
  type ProjectUserListResponse,
  type ProjectUserResponse,
  type UpdateProjectUserPayload,
} from './project-service.types';

export function listProjects({ organizationId }: OrganizationContext, params: QueryOptions = defaultPaginationQuery) {
  return getRequest<ProjectListResponse>(`/v2/organizations/${organizationId}/projects`, { params });
}

/**
 * Return a specified project.
 */
export function getProject({ projectId, organizationId }: ProjectContext) {
  return getRequest<ProjectResponse>(`/v2/organizations/${organizationId}/projects/${projectId}`);
}

/**
 * Create a project in a specified organization.
 */
export function createProject({ organizationId }: OrganizationContext, data: ProjectCreationPayload) {
  return postRequest<ProjectCreationResponse>(`/v2/organizations/${organizationId}/projects`, { data });
}

/**
 * Update a project in a specified organization.
 */
export function updateProject({ projectId, organizationId }: ProjectContext, data: ProjectUpdatePayload) {
  return putRequest<{}>(`/v2/organizations/${organizationId}/projects/${projectId}`, {
    data,
  });
}

/**
 * Delete a project in a specified organization.
 */
export function deleteProject({ projectId, organizationId }: ProjectContext) {
  return deleteRequest<{}>(`/v2/organizations/${organizationId}/projects/${projectId}`);
}

/**
 * Return a list of users for a specified project.
 */
export function listProjectUsers({ projectId, organizationId }: ProjectContext, params: QueryOptions = defaultPaginationQuery) {
  return getRequest<ProjectUserListResponse>(`/v2/organizations/${organizationId}/projects/${projectId}/users`, { params });
}

/**
 * Return a specified user for a given project.
 */
export function getProjectUser({ userId, projectId, organizationId }: ProjectUserContext) {
  return getRequest<ProjectUserResponse>(`/v2/organizations/${organizationId}/projects/${projectId}/users/${userId}`);
}

/**
 * Creates a user for a given project.
 */
export function createProjectUsers({ organizationId }: OrganizationContext, data: UpdateProjectUserPayload) {
  return putRequest<{}>(`/v2/organizations/${organizationId}/permissions`, { data });
}

/**
 * Updates user's permissions for a given project.
 */
export function updateProjectUsers({ organizationId }: OrganizationContext, data: UpdateProjectUserPayload) {
  return putRequest<{}>(`/v2/organizations/${organizationId}/permissions`, { data });
}

/**
 * Delete a user from a specified project.
 */
export function deleteProjectUser({ userId, projectId, organizationId }: ProjectUserContext) {
  return deleteRequest<ProjectResponse>(`/v2/organizations/${organizationId}/permissions/${userId}/resource/${projectId}`);
}

/**
 * Return a list of available users to add to a given project.
 */
export function getAvailableProjectInvitees({ projectId, organizationId }: ProjectContext) {
  return getRequest<AvailableInviteesResponse>(`/v2/organizations/${organizationId}/projects/${projectId}/available-invitees`);
}

/**
 * Return a list of integrations for a specified project.
 */
export function listProjectIntegrations({ projectId, organizationId }: ProjectContext, params: QueryOptions = defaultPaginationQuery) {
  return getRequest<ProjectIntegrationListResponse>(`/v2/organizations/${organizationId}/projects/${projectId}/alerts`, { params });
}

export function createProjectIntegration({ projectId, organizationId }: ProjectContext, data: CreateProjectIntegrationPayload) {
  return postRequest<CreateProjectIntegrationResponse>(`/v2/organizations/${organizationId}/projects/${projectId}/alerts`, { data });
}

export function getProjectIntegration({ projectId, organizationId, integrationId }: ProjectIntegrationContext) {
  return getRequest<ProjectIntegration>(`/v2/organizations/${organizationId}/projects/${projectId}/alerts/${integrationId}`);
}

export function editProjectIntegration(
  { projectId, organizationId, integrationId }: ProjectIntegrationContext,
  data: CreateProjectIntegrationPayload
) {
  return putRequest<{}>(`/v2/organizations/${organizationId}/projects/${projectId}/alerts/${integrationId}`, { data });
}

export function deleteProjectIntegration({ projectId, organizationId, integrationId }: ProjectIntegrationContext) {
  return deleteRequest<{}>(`/v2/organizations/${organizationId}/projects/${projectId}/alerts/${integrationId}`);
}

export function testConnection({ projectId, organizationId }: ProjectContext, data: CreateProjectIntegrationPayload) {
  return postRequest<{}>(`v2/organizations/${organizationId}/projects/${projectId}/alerts/test`, { data });
}

export function listProjectAPIKeys({ organizationId, projectId }: ProjectContext, query: QueryOptions = defaultPaginationQuery) {
  return getRequest<ProjectAPIKeyListResponse>(`/v2/organizations/${organizationId}/projects/${projectId}/apikeys`, { params: query });
}

export function getProjectAPIKey({ organizationId, projectId, keyId }: ProjectAPIKeyContext) {
  return getRequest<ProjectAPIKeyResponse>(`/v2/organizations/${organizationId}/projects/${projectId}/apikeys/${keyId}`);
}

export function createProjectAPIKey({ organizationId, projectId }: ProjectContext, payload: ProjectAPIKeyCreationPayload) {
  return postRequest<ProjectAPIKeyCreateResponse>(`/v2/organizations/${organizationId}/projects/${projectId}/apikeys`, {
    data: payload,
  });
}

export function deleteProjectAPIKey({ organizationId, projectId, keyId }: ProjectAPIKeyContext) {
  return deleteRequest<{}>(`/v2/organizations/${organizationId}/projects/${projectId}/apikeys/${keyId}`);
}

export function listProjectLevelBackups({ organizationId, projectId }: ProjectContext, query: QueryOptions = defaultPaginationQuery) {
  return getRequest<ProjectClusterSnapshotListResponse>(
    `/v2/organizations/${organizationId}/projects/${projectId}/provisioned/cloudsnapshotbackups`,
    {
      params: query,
    }
  );
}
